import { Timestamp } from "firebase/firestore";
import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminPageLayout from "../../../components/AdminPageLayout/AdminPageLayout";
import AdminUserSelector from "../../../components/AdminUserSelector/AdminUserSelector";
import ErrorDisplay from "../../../components/ErrorDisplay/ErrorDisplay";
import { Headings } from "../../../components/Headings";
import LoadingMask from "../../../components/LoadingMask/LoadingMask";
import VibeSelector from "../../../components/VibeSelector/VibeSelector";
import {
  deletePlace,
  getPlaceById,
  updatePlace,
} from "../../../services/placeService";
import { PaymentType } from "../../../types/PaymentType";
import { Place } from "../../../types/Place";
import { User } from "../../../types/User";
import { UserAttributableText } from "../../../types/UserAttributableText";
import { VibeType } from "../../../types/VibeType";
import { debugLogError } from "../../../utils/debug-log-error";
import { formatTimestamp } from "../../../utils/format-timestamp";
import "./AdminPlaceEditPage.scss";

type PlaceFormData = Omit<Place, "createdAt" | "googlePlaceId"> & {
  createdAt?: Timestamp;
  googlePlaceId?: string;
  vibes?: VibeType[];
  vibeSummary?: UserAttributableText;
  paymentTypes?: PaymentType[];
};

const AdminPlaceEditPage: React.FC = () => {
  const { placeId } = useParams<{ placeId: string }>();
  const navigate = useNavigate();

  const [place, setPlace] = useState<Place | null>(null);
  const [formData, setFormData] = useState<Partial<PlaceFormData>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  useEffect(() => {
    if (!placeId) {
      setError("No place ID provided.");
      setIsLoading(false);
      return;
    }

    const fetchPlace = async () => {
      setIsLoading(true);
      try {
        const fetchedPlace = await getPlaceById(placeId);
        if (fetchedPlace) {
          setPlace(fetchedPlace);

          // Initialize formData with all editable fields from fetchedPlace
          const { googlePlaceId, createdAt, ...editableData } = fetchedPlace;
          setFormData({
            name: editableData.name || "",
            address: editableData.address || "",
            lat: editableData.lat || 0,
            lng: editableData.lng || 0,
            website: editableData.website || "",
            numberOfTables: editableData.numberOfTables || 0,
            checkedInUserIds: editableData.checkedInUserIds || [],
            vibes: editableData.vibes || [],
            vibeSummary: editableData.vibeSummary || { text: "", author: "" },
            paymentTypes: editableData.paymentTypes || [],
          });
          setError(null);
        } else {
          setError(`Place with ID ${placeId} not found.`);
          setPlace(null);
        }
      } catch (err) {
        debugLogError("AdminPlaceEditPage", err);
        setError("Failed to fetch place details.");
        setPlace(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlace();
  }, [placeId]);

  const handleUserSelected = (user: User) => {
    setFormData((prev) => ({
      ...prev,
      vibeSummary: {
        ...prev.vibeSummary,
        author: user.uid,
        text: prev.vibeSummary?.text || "",
      },
    }));
  };

  const handleVibeChange = (vibes: VibeType[]) => {
    setFormData((prev) => ({
      ...prev,
      vibes,
    }));
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (name === "vibes") {
      const { value: vibeValue, checked } = e.target as HTMLInputElement;
      setFormData((prev) => {
        const currentVibes = prev.vibes || [];
        if (checked) {
          return { ...prev, vibes: [...currentVibes, vibeValue as VibeType] };
        } else {
          return {
            ...prev,
            vibes: currentVibes.filter((v) => v !== vibeValue),
          };
        }
      });
    } else if (name === "paymentTypes") {
      const { value: paymentTypeValue, checked } = e.target as HTMLInputElement;
      setFormData((prev) => {
        const currentPaymentTypes = prev.paymentTypes || [];
        if (checked) {
          return {
            ...prev,
            paymentTypes: [
              ...currentPaymentTypes,
              paymentTypeValue as PaymentType,
            ],
          };
        } else {
          return {
            ...prev,
            paymentTypes: currentPaymentTypes.filter(
              (pt) => pt !== paymentTypeValue
            ),
          };
        }
      });
    } else if (name.startsWith("vibeSummary.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        vibeSummary: {
          ...(prev.vibeSummary || { text: "", author: "" }),
          [field]: value,
        },
      }));
    } else {
      // existing logic for other fields
      let processedValue: string | number = value;
      if (type === "number") {
        processedValue = value === "" ? "" : Number(value);
      }
      setFormData((prev) => ({
        ...prev,
        [name]: processedValue,
      }));
    }
  };

  const handleSave = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!placeId || !place) {
        setError("Cannot save, place data is missing.");
        return;
      }

      setIsSaving(true);
      setError(null);

      // Construct the data to save, ensuring type compatibility with Partial<Place>
      const dataToUpdate: Partial<Place> = { ...formData };

      if (formData.lat !== undefined) {
        dataToUpdate.lat = Number(formData.lat) || 0;
      }

      if (formData.lng !== undefined) {
        dataToUpdate.lng = Number(formData.lng) || 0;
      }

      if (formData.numberOfTables !== undefined) {
        dataToUpdate.numberOfTables = Number(formData.numberOfTables) || 0;
      }

      if (formData.vibes) {
        dataToUpdate.vibes = formData.vibes;
      }

      if (formData.vibeSummary) {
        dataToUpdate.vibeSummary = formData.vibeSummary;
      }

      if (formData.paymentTypes) {
        dataToUpdate.paymentTypes = formData.paymentTypes;
      }

      try {
        await updatePlace(placeId, dataToUpdate);

        toast.success("Place updated successfully!");
        navigate("/admin/places");
      } catch (err) {
        debugLogError("AdminPlaceEditPage", err);
        setError("Failed to update place. Please try again.");
      } finally {
        setIsSaving(false);
      }
    },
    [placeId, place, formData, navigate]
  );

  const handleDelete = useCallback(async () => {
    if (!placeId) {
      setError("Cannot delete, place ID is missing.");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${place?.name || "this place"}?`
      )
    ) {
      setIsDeleting(true);
      setError(null);
      try {
        await deletePlace(placeId);
        toast.success("Place deleted successfully!");
        navigate("/admin/places");
      } catch (err) {
        debugLogError("AdminPlaceEditPage", err);
        setError("Failed to delete place. Please try again.");
      } finally {
        setIsDeleting(false);
      }
    }
  }, [placeId, place, navigate]);

  if (isLoading) {
    return <LoadingMask message="Loading place details..." />;
  }

  return (
    <AdminPageLayout title={`Edit Place: ${place?.name}`}>
      <div className="admin-place-edit-page-content">
        {error && <ErrorDisplay error={error} />}
        {!isLoading && !error && place && (
          <>
            <div className="gapped-buttons">
              <Link to="/admin/places" className="btn btn-outline-primary">
                &larr; Admin: Places
              </Link>
              <Link
                to={`/place/${place.googlePlaceId}`}
                className="btn btn-outline-primary"
              >
                View Place Page
              </Link>
            </div>
            <Headings.Page>Edit Place: {place.name}</Headings.Page>
            <form onSubmit={handleSave} className="edit-place-form">
              <div className="form-readonly-fields">
                <p>
                  <strong>Google Place ID:</strong>{" "}
                  {place.googlePlaceId || "N/A"}
                </p>
                <p>
                  <strong>Created At:</strong>{" "}
                  {formatTimestamp(place.createdAt)}
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={formData.name || ""}
                  onChange={handleChange}
                  required
                  disabled={isSaving || isDeleting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  className="form-control"
                  value={formData.address || ""}
                  onChange={handleChange}
                  required
                  rows={3}
                  disabled={isSaving || isDeleting}
                />
              </div>

              <div className="form-group-inline">
                <div className="form-group">
                  <label htmlFor="lat">Latitude</label>
                  <input
                    type="number"
                    id="lat"
                    name="lat"
                    className="form-control"
                    value={formData.lat === undefined ? "" : formData.lat}
                    onChange={handleChange}
                    step="any"
                    required
                    disabled={isSaving || isDeleting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lng">Longitude</label>
                  <input
                    type="number"
                    id="lng"
                    name="lng"
                    className="form-control"
                    value={formData.lng === undefined ? "" : formData.lng}
                    onChange={handleChange}
                    step="any"
                    required
                    disabled={isSaving || isDeleting}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  className="form-control"
                  name="website"
                  value={formData.website || ""}
                  onChange={handleChange}
                  disabled={isSaving || isDeleting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="numberOfTables">Number of Tables</label>
                <input
                  type="number"
                  id="numberOfTables"
                  className="form-control"
                  name="numberOfTables"
                  value={
                    formData.numberOfTables === undefined
                      ? ""
                      : formData.numberOfTables
                  }
                  onChange={handleChange}
                  min="0"
                  disabled={isSaving || isDeleting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="vibes">Vibes</label>
                <VibeSelector
                  value={formData.vibes ?? []}
                  onChange={handleVibeChange}
                  disabled={isSaving || isDeleting}
                />
              </div>

              <div className="form-group">
                <label>Payment Types</label>
                <div>
                  {Object.values(PaymentType).map((pt) => (
                    <div key={pt} className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`paymentType-${pt}`}
                        name="paymentTypes"
                        value={pt}
                        checked={formData.paymentTypes?.includes(pt) || false}
                        onChange={handleChange}
                        disabled={isSaving || isDeleting}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={`paymentType-${pt}`}
                      >
                        {pt}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="vibeSummaryAuthor">
                  Vibe Summary Author (User ID)
                </label>
                <AdminUserSelector onUserSelected={handleUserSelected} />
                {!!formData.vibeSummary?.author && (
                  <span className="form-caption">
                    Value: {formData.vibeSummary.author}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="vibeSummaryText">Vibe Summary Text</label>
                <textarea
                  id="vibeSummaryText"
                  name="vibeSummary.text"
                  className="form-control"
                  value={formData.vibeSummary?.text || ""}
                  onChange={handleChange}
                  rows={3}
                  disabled={isSaving || isDeleting}
                />
              </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSaving || isDeleting}
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={isSaving || isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete Place"}
                </button>
              </div>
            </form>
          </>
        )}
        {!isLoading && !place && !error && (
          <div className="alert alert-danger">
            Place data could not be loaded.
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default AdminPlaceEditPage;
